/**
 * API service for practitioner-to-user mapping endpoints.
 */

import { authFetch, apiErrorMessage } from './authFetch';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

export interface TriggerMappingRequest {
    gh_id: string;
    batch_size: number;
    practitioner_id?: string;
}

export interface TriggerMappingResponse {
    status: string;
    message: string;
    suggestions_created: number;
}

export interface MappingSuggestion {
    id: number;
    gh_id: string;
    emr_source: string;
    practitioner_external_id: string;
    practitioner_name: string | null;
    practitioner_npi: string | null;
    practitioner_specialty: string | null;
    suggested_user_id: number | null;
    suggested_user_name: string | null;
    suggested_user_email: string | null;
    confidence_score: number;
    reasoning: string;
    risk_factors: string[] | null;
    alternative_matches: any[] | null;
    status: string;
    is_reviewed: boolean;
    reviewed_by: number | null;
    reviewed_by_name: string | null;
    reviewed_at: string | null;
    created_at: string;
    is_external?: boolean;
}

export interface SuggestionsResponse {
    total: number;
    suggestions: MappingSuggestion[];
}

export interface ReviewRequest {
    action: 'approve' | 'reject';
    notes?: string;
}

export interface ReviewResponse {
    status: string;
    message: string;
}

export interface StatsResponse {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    high_confidence: number;
    medium_confidence: number;
    low_confidence: number;
}

export const practitionerMappingApi = {
    /**
     * Trigger practitioner mapping for a GHL location
     */
    async triggerMapping(request: TriggerMappingRequest): Promise<TriggerMappingResponse> {
        const response = await authFetch(`${API_BASE_URL}/practitioner-mapping/trigger/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(request)
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(apiErrorMessage(error, 'Failed to trigger mapping'));
        }

        return response.json();
    },

    /**
     * Get mapping suggestions for a GHL location
     */
    async getSuggestions(
        gh_id: string,
        filters?: {
            status?: string;
            min_confidence?: number;
            max_confidence?: number;
            limit?: number;
            offset?: number;
            practitioner_name?: string;
            user_name?: string;
            search?: string;
        }
    ): Promise<SuggestionsResponse> {
        const params = new URLSearchParams();
        if (filters?.status) params.append('status', filters.status);
        if (filters?.min_confidence !== undefined) params.append('min_confidence', filters.min_confidence.toString());
        if (filters?.max_confidence !== undefined) params.append('max_confidence', filters.max_confidence.toString());
        if (filters?.limit) params.append('limit', filters.limit.toString());
        if (filters?.offset) params.append('offset', filters.offset.toString());
        if (filters?.practitioner_name) params.append('practitioner_name', filters.practitioner_name);
        if (filters?.user_name) params.append('user_name', filters.user_name);
        if (filters?.search) params.append('search', filters.search);

        const queryString = params.toString();
        const url = `${API_BASE_URL}/practitioner-mapping/suggestions/${gh_id}${queryString ? '?' + queryString : ''}`;

        const response = await authFetch(url, { method: 'GET' });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(apiErrorMessage(error, 'Failed to fetch suggestions'));
        }

        return response.json();
    },

    /**
     * Get distinct values per filterable column, for populating dropdown filters.
     */
    async getFilterOptions(gh_id: string): Promise<{ practitioner_name: string[]; user_name: string[] }> {
        const response = await authFetch(`${API_BASE_URL}/practitioner-mapping/filter-options/${gh_id}`);
        if (!response.ok) throw new Error('Failed to fetch filter options');
        return response.json();
    },

    /**
     * Review a mapping suggestion
     */
    async reviewSuggestion(
        suggestionId: number,
        request: ReviewRequest
    ): Promise<ReviewResponse> {
        const response = await authFetch(`${API_BASE_URL}/practitioner-mapping/review/${suggestionId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(request)
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(apiErrorMessage(error, 'Failed to review suggestion'));
        }

        return response.json();
    },

    /**
     * Permanently delete a mapping suggestion
     */
    async deleteSuggestion(suggestionId: number): Promise<void> {
        const response = await authFetch(`${API_BASE_URL}/practitioner-mapping/${suggestionId}`, {
            method: 'DELETE',
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(apiErrorMessage(error, 'Failed to delete suggestion'));
        }
    },

    /**
     * Get mapping statistics for a GHL location
     */
    async getStats(gh_id: string): Promise<StatsResponse> {
        const response = await authFetch(`${API_BASE_URL}/practitioner-mapping/stats/${gh_id}`, {
            method: 'GET'
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(apiErrorMessage(error, 'Failed to fetch stats'));
        }

        return response.json();
    }
};

// Manual mapping interfaces and methods
export interface GHLUser {
    id: number;
    name: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
}

export interface EMRPractitioner {
    external_id: string;
    name: string;
    npi?: string;
    specialty?: string;
    emr_source: string;
}

export interface ManualPractitionerMappingRequest {
    gh_id: string;
    practitioner_external_id: string;
    practitioner_name: string;
    user_id: number;
    user_name: string;
}

export const practitionerMappingManualApi = {
    /**
     * Get all GHL users for manual mapping
     */
    async getGHLUsers(ghId: string): Promise<GHLUser[]> {
        const response = await authFetch(`${API_BASE_URL}/practitioner-mapping/ghl-users/${ghId}`);

        if (!response.ok) {
            throw new Error('Failed to fetch Advital users');
        }

        const data = await response.json();
        return data.users;
    },

    /**
     * Get all EMR practitioners for manual mapping
     */
    async getEMRPractitioners(ghId: string): Promise<EMRPractitioner[]> {
        const response = await authFetch(`${API_BASE_URL}/practitioner-mapping/emr-practitioners/${ghId}`);

        if (!response.ok) {
            throw new Error('Failed to fetch EMR practitioners');
        }

        const data = await response.json();
        return data.practitioners;
    },

    /**
     * Create a manual mapping (standalone Manual Mapping page)
     */
    async createManualMapping(request: ManualPractitionerMappingRequest): Promise<void> {
        const response = await authFetch(`${API_BASE_URL}/practitioner-mapping/manual`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(apiErrorMessage(error, 'Failed to create manual mapping'));
        }
    },

    /**
     * Update an existing mapping row (Edit button on mappings table)
     */
    async updateManualMapping(
        suggestionId: number,
        request: Pick<ManualPractitionerMappingRequest, 'user_id' | 'user_name' | 'practitioner_name'>
    ): Promise<void> {
        const response = await authFetch(`${API_BASE_URL}/practitioner-mapping/manual/${suggestionId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(apiErrorMessage(error, 'Failed to update manual mapping'));
        }
    },
};
